import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Calendar,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

const NutritionSummary = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  const weeklyData = [
    { day: "Mon", calories: 1850, protein: 92, carbs: 180, fat: 58, fiber: 28 },
    { day: "Tue", calories: 1920, protein: 98, carbs: 175, fat: 65, fiber: 32 },
    { day: "Wed", calories: 1750, protein: 85, carbs: 165, fat: 52, fiber: 25 },
    {
      day: "Thu",
      calories: 1980,
      protein: 105,
      carbs: 190,
      fat: 68,
      fiber: 35,
    },
    {
      day: "Fri",
      calories: 2100,
      protein: 110,
      carbs: 200,
      fat: 72,
      fiber: 38,
    },
    { day: "Sat", calories: 1890, protein: 95, carbs: 182, fat: 60, fiber: 30 },
    { day: "Sun", calories: 1740, protein: 98, carbs: 175, fat: 63, fiber: 29 },
  ];

  // Monthly nutrients data (commented out as unused)
  // const monthlyNutrients = [...];

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Unused function commented out
  // const getStatusColor = (status: string) => { ... };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image - Only within container */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast - Only within container */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-10 w-10 rounded-xl border-border bg-card"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Nutrition Summary
              </h1>
              <p className="text-sm text-muted-foreground">Weekly Overview</p>
            </div>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-xl border-border bg-card"
              >
                <Calendar className="h-5 w-5 text-orange-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="z-50 w-80 rounded-lg border bg-card p-0 shadow-lg"
              align="end"
            >
              {/* Calendar Header */}
              <div className="flex items-center justify-between border-b border-border p-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={goToPreviousMonth}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-sm font-semibold">
                  {currentMonth.toLocaleDateString("en-US", {
                    month: "long",
                    year: "numeric",
                  })}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={goToNextMonth}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="p-3">
                {/* Days of Week Header */}
                <div className="mb-2 grid grid-cols-7 gap-1">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    day => (
                      <div
                        key={day}
                        className="p-2 text-center text-xs font-medium text-muted-foreground"
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <div key={index} className="aspect-square">
                      {day ? (
                        <button
                          className={`flex h-full w-full items-center justify-center rounded-md text-xs transition-colors ${
                            isToday(day)
                              ? "bg-orange-500 text-white"
                              : "hover:bg-muted"
                          }`}
                        >
                          {day.getDate()}
                        </button>
                      ) : (
                        <div />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Today's Stats */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">
            Today's Intake
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-muted/30 p-4">
              <div className="mb-1 text-3xl font-bold text-foreground">
                1,740
              </div>
              <div className="text-xs text-muted-foreground">Calories</div>
              <div className="mt-2 flex items-center gap-1">
                <TrendingDown className="h-3 w-3 text-red-500" />
                <span className="text-xs text-red-500">-13% vs goal</span>
              </div>
            </div>
            <div className="rounded-xl bg-muted/30 p-4">
              <div className="mb-1 text-3xl font-bold text-chart-1">98g</div>
              <div className="text-xs text-muted-foreground">Protein</div>
              <div className="mt-2 flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-green-500" />
                <span className="text-xs text-green-500">+2% vs goal</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Calorie Trend Chart */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">
            Weekly Calorie Trend
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="calories"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--primary))", r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Macros Breakdown */}
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">
            Weekly Macros Average
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-chart-1" />
                <span className="text-sm text-foreground">Protein</span>
              </div>
              <span className="text-lg font-bold text-chart-1">97.5g</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-chart-2" />
                <span className="text-sm text-foreground">Carbohydrates</span>
              </div>
              <span className="text-lg font-bold text-chart-2">181g</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-chart-3" />
                <span className="text-sm text-foreground">Fat</span>
              </div>
              <span className="text-lg font-bold text-chart-3">62.5g</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-chart-4" />
                <span className="text-sm text-foreground">Fiber</span>
              </div>
              <span className="text-lg font-bold text-chart-4">31.1g</span>
            </div>
          </div>
        </Card>

        {/* Weekly Macros Chart 
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">Weekly Macros Distribution</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                <XAxis 
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px"
                  }}
                />
                <Bar dataKey="protein" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} />
                <Bar dataKey="carbs" fill="hsl(var(--chart-2))" radius={[2, 2, 0, 0]} />
                <Bar dataKey="fat" fill="hsl(var(--chart-3))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        */}

        {/* Micronutrients Status 
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">Micronutrients Status</h3>
          <div className="space-y-3">
            {monthlyNutrients.map((nutrient, index) => (
              <div key={index} className="flex items-center justify-between rounded-lg border border-border bg-muted/20 p-3">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{nutrient.nutrient}</span>
                    <span className="text-sm font-bold text-foreground">
                      {nutrient.value}{nutrient.unit}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Goal: {nutrient.goal}{nutrient.unit}
                  </div>
                </div>
                <div className={`ml-3 rounded-full border px-2 py-1 text-xs font-medium ${getStatusColor(nutrient.status)}`}>
                  {nutrient.status === "good" && "Good"}
                  {nutrient.status === "moderate" && "Moderate"}
                  {nutrient.status === "low" && "Low"}
                </div>
              </div>
            ))}
          </div>
        </Card>
        */}

        {/* Quick Actions 
        <Card className="rounded-xl border-2 border-border bg-card p-5 shadow-md">
          <h3 className="mb-4 text-lg font-bold text-foreground">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              className="h-12 rounded-xl border-2 font-medium"
              onClick={() => navigate("/glucera/mealpage")}
            >
              View Meals
            </Button>
            <Button 
              variant="outline" 
              className="h-12 rounded-xl border-2 font-medium"
              onClick={() => navigate("/glucera/add-meal")}
            >
              Add Meal
            </Button>
          </div>
        </Card>
        */}
      </div>

      <BottomNavBar activeIndex={1} />
    </div>
  );
};

export default NutritionSummary;
